import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";

import Input from "components/atoms/Input/Input";
import Radio from "components/atoms/Radio/Radio";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { SERVICE_REQUIREMENT_LABEL_MAPPING } from "helpers/quotationFormHelper";
import { INCOTERM_OPTIONS } from "../../../../../shipper/RequestForm/components/Service/ServiceFormDefaultOptions";

interface ServiceSpecification {
  export_clearance_required: boolean;
  transit_clearance_required: boolean;
  import_clearance_required: boolean;
}

interface ServiceRequirement {
  dtd_required: boolean;
  dtp_required: boolean;
  ptd_required: boolean;
  ptp_required: boolean;
}

interface ServiceObject {
  service_specification: ServiceSpecification;
  service_requirement: ServiceRequirement;
  [key: string]: any;
}

type ServiceQuestion = {
  label: string;
  value: string;
};

function ServiceConfirmation({ quotationConfigData }: { quotationConfigData: { service_confirmation: ServiceObject } }) {
  const intl = useIntl();
  const { watch, getValues } = useFormContext();

  const serviceConfirmationAnswer = (questionValue: string) => watch(`service_confirmation.defaults.${questionValue}.value`);
  const serviceRequirementAnswer = watch("service_confirmation.defaults.service_type.value");
  const incotermAnswer = watch("service_confirmation.defaults.incoterm.value");

  const serviceOptions = [
    {
      value: "true",
      label: intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" }),
    },
    {
      value: "false",
      label: intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
    },
  ];

  const mapServiceConfigToQuestions = (acc: ServiceQuestion[], [key, show]: [key: string, show: any]) => {
    if (!show) return acc;
    const value = key.endsWith("_required") ? key.slice(0, -9) : key;
    const label =
      SERVICE_REQUIREMENT_LABEL_MAPPING[value] ?? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    return [...acc, { label, value }];
  };

  const serviceRequirementQuestions = Object.entries(
    quotationConfigData?.service_confirmation?.service_requirement ?? {},
  ).reduce<ServiceQuestion[]>(mapServiceConfigToQuestions, []);

  const serviceSpecificationQuestions = Object.entries(
    quotationConfigData?.service_confirmation?.service_specification ?? {},
  ).reduce<ServiceQuestion[]>(mapServiceConfigToQuestions, []);

  const incotermLabel = INCOTERM_OPTIONS(intl).find(
    (option) => option.value === quotationConfigData.service_confirmation?.incoterm,
  )?.label;

  return (
    <Row className="mb-24">
      <Col lg={2} md={3}>
        <h6>
          <FormattedMessage
            id="shipment-quotes.components.quotes.serviceConfirmation"
            defaultMessage="Service Confirmation"
          />
        </h6>
      </Col>
      <Col lg={6} md={9}>
        {serviceSpecificationQuestions.map((question, index) => (
          <Row key={index} className="mb-3">
            <Col lg={4}>
              <label className="required font-weight-bold">{question.label}</label>
            </Col>
            <Col lg={8}>
              <Radio
                options={serviceOptions}
                name={`service_confirmation.defaults.${question.value}.value`}
                customOption
                className="border-0 w-50 task-dialog-radio"
                eventContext="Task Dialog"
                defaultChecked={getValues(`service_confirmation.defaults.${question.value}.value`)}
                required
              />
              {serviceConfirmationAnswer(question.value) === "false" && (
                <div>
                  <Input
                    type="text"
                    name={`service_confirmation.defaults.${question.value}.comment`}
                    placeholder={intl.formatMessage({
                      id: "billing.newCommentPlaceholder",
                      defaultMessage: "Optional comment",
                    })}
                    eventContext="Task Dialog"
                    className="rounded-pill input-width-lg h-26"
                  />
                </div>
              )}
            </Col>
          </Row>
        ))}
        {serviceRequirementQuestions.map((question, index) => (
          <Row key={index} className="mb-3">
            <Col lg={4}>
              <label className="required font-weight-bold">{question.label}</label>
            </Col>
            <Col lg={8}>
              <Radio
                options={serviceOptions}
                name="service_confirmation.defaults.service_type.value"
                customOption
                className="border-0 w-50 task-dialog-radio"
                eventContext="Task Dialog"
                defaultChecked={getValues(`service_confirmation.defaults.service_type.value`)}
                required
              />
              {serviceRequirementAnswer === "false" && (
                <div>
                  <Input
                    type="text"
                    name="service_confirmation.defaults.service_type.comment"
                    placeholder={intl.formatMessage({
                      id: "billing.newCommentPlaceholder",
                      defaultMessage: "Optional comment",
                    })}
                    eventContext="Task Dialog"
                    className="rounded-pill input-width-lg h-26"
                  />
                </div>
              )}
            </Col>
          </Row>
        ))}
        {quotationConfigData.service_confirmation?.incoterm && (
          <Row>
            <Col lg={4}>
              <label className="required font-weight-bold">
                <FormattedMessage id="quotation-form.incoterm" defaultMessage="Incoterm" />: {incotermLabel}
              </label>
            </Col>
            <Col lg={8}>
              <Radio
                options={serviceOptions}
                name="service_confirmation.defaults.incoterm.value"
                defaultChecked={getValues("service_confirmation.defaults.incoterm.value")}
                customOption
                className="border-0 w-50 task-dialog-radio"
                eventContext="Task Dialog"
                // defaultChecked={getValues(`service_confirmation.defaults.incoterm.value`)}
                required
              />
              {incotermAnswer === "false" && (
                <div>
                  <Dropdown
                    useHookForm
                    options={INCOTERM_OPTIONS(intl)}
                    name="service_confirmation.defaults.incoterm.selected_incoterm"
                    placeholder={intl.formatMessage({
                      id: "general.select",
                      defaultMessage: "Select",
                    })}
                    shouldUnregister
                    required
                  />
                  <Input
                    type="text"
                    name="service_confirmation.defaults.incoterm.comment"
                    placeholder={intl.formatMessage({
                      id: "billing.newCommentPlaceholder",
                      defaultMessage: "Optional comment",
                    })}
                    eventContext="Task Dialog"
                    className="rounded-pill input-width-lg h-26 mt-3"
                  />
                </div>
              )}
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
}

export default ServiceConfirmation;
